import { inject, Injectable, OnDestroy } from '@angular/core';
import { ApplicationStateService } from './application-state.service';
import { PageEditModeFactory } from '../utils/page-edit-modes/page-edit-mode-factory';
import { catchError, Observable, Subscription, tap } from 'rxjs';
import { ModalService } from './modal.service';
import { ModalableWhiteboardDrawing } from '../models/modalable-whiteboard-drawing';
import { ModalableWhiteboardVideo } from '../models/modalable-whiteboard-video';
import { WhiteboardDrawingsService, WhiteboardEventsService } from '@lemberger/whiteboards';
import { WhiteboardDrawing } from '../models/whiteboard-drawing';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { RouteService } from './route.service';
import { CrudService } from './crud.service';
import { TechnicalFeature } from '@lemberger/lemberger-am';

@Injectable({
    providedIn: 'root'
})
export class BookviewerWhiteboardDrawingsService implements OnDestroy {
    private subscription: Subscription;
    private closeSubscription: Subscription;
    private openSubscription: Subscription;
    private openVideoSubscription: Subscription;
    private crudService = inject(CrudService);

    constructor(
        private applicationStateService: ApplicationStateService,
        private modeFactory: PageEditModeFactory,
        private httpClient: HttpClient,
        private routeService: RouteService,
        private whiteboardDrawingsService: WhiteboardDrawingsService,
        private whiteboardEventService: WhiteboardEventsService,
        private modalService: ModalService) {

        this.subscription = this.whiteboardEventService.whiteboardLinking$.subscribe(whiteboard => {
            if (whiteboard) {
                this.applicationStateService.pageEditMode = this.modeFactory
                    .createWhiteboardEditMode(whiteboard as WhiteboardDrawing, this);
            }
        });

        this.closeSubscription = this.whiteboardEventService.closeWhiteboardModal$.subscribe(() => {
            this.modalService.closeModal(new ModalableWhiteboardDrawing().getModalId());
        });
        this.openSubscription = this.whiteboardEventService.openWhiteboardModal$.subscribe(whiteboard => {
            const modalable = Object.assign(new ModalableWhiteboardDrawing(), whiteboard);
            this.modalService.openModal(modalable);
        });
        this.openVideoSubscription = this.whiteboardEventService.openWhiteboardVideoModal$.subscribe(whiteboard => {
            const modalable = Object.assign(new ModalableWhiteboardVideo(), whiteboard);
            this.modalService.openModal(modalable);
        });

    }

    getWhiteboards(book: number): Observable<Array<WhiteboardDrawing>> {
        const route = this.routeService.getAbsoluteRoute(environment.endpointUrls.whiteboardDrawingList,
            { bookId: this.applicationStateService.bookId }
        );

        return this.httpClient.get<Array<WhiteboardDrawing>>(route)

            .pipe(
                catchError((err) => {
                    this.whiteboardDrawingsService.setWhiteboardsInSubject(this.whiteboardDrawingsService.whiteboardDrawings);

                    return [];
                }),
                tap(drawings => {
                    this.whiteboardDrawingsService.whiteboardDrawings = drawings;
                    this.whiteboardDrawingsService.whiteboardDrawings.map(drawing => {
                        const castedDrawing = new WhiteboardDrawing();
                        Object.assign(castedDrawing, drawing);

                        return castedDrawing;
                    });
                    this.whiteboardDrawingsService.setWhiteboardsInSubject(this.whiteboardDrawingsService.whiteboardDrawings);
                })
            );
    }

    updateWhiteboard(book: number, whiteboard: WhiteboardDrawing): Observable<WhiteboardDrawing> {
        this.crudService.logDirect(TechnicalFeature.Whiteboard);

        const route = this.routeService.getAbsoluteRoute(
            environment.endpointUrls.whiteboardDrawingUpdate, { bookId: book, id: whiteboard.id });

        return this.httpClient.patch<WhiteboardDrawing>(route, whiteboard)
            .pipe(tap(updatedDrawing => {
                    const index = this.whiteboardDrawingsService.whiteboardDrawings
                        .findIndex(whiteboard => whiteboard.id === updatedDrawing.id);
                    const castedCreatedDrawing = new WhiteboardDrawing();
                    Object.assign(castedCreatedDrawing, updatedDrawing);
                    this.whiteboardDrawingsService.whiteboardDrawings[index] = castedCreatedDrawing;
                    this.whiteboardDrawingsService.setWhiteboardsInSubject(this.whiteboardDrawingsService.whiteboardDrawings);
                })
            );
    }

    updateWhiteboardPosition(book: number, whiteboard: WhiteboardDrawing): Observable<WhiteboardDrawing> {
        this.crudService.logDirect(TechnicalFeature.Whiteboard);

        const route = this.routeService.getAbsoluteRoute(
            environment.endpointUrls.whiteboardDrawingUpdate, { bookId: book, id: whiteboard.id }
        );

        return this.httpClient.patch<WhiteboardDrawing>(route, whiteboard);
    }

    deleteWhiteboard(book: number, whiteboard: WhiteboardDrawing): Observable<string> {
        this.crudService.logDirect(TechnicalFeature.Whiteboard);

        const route = this.routeService.getAbsoluteRoute(
            environment.endpointUrls.whiteboardDrawingDelete, { bookId: book, id: whiteboard.id });

        return this.httpClient.delete<string>(route).pipe(
            tap(deletedWhiteboardId => {
                this.whiteboardDrawingsService.whiteboardDrawings = this.whiteboardDrawingsService.whiteboardDrawings
                    .filter(whiteboard => whiteboard.id !== deletedWhiteboardId);
                this.whiteboardDrawingsService.setWhiteboardsInSubject(this.whiteboardDrawingsService.whiteboardDrawings);
            })
        );
    }

    downloadVideo(book: number, whiteboard: WhiteboardDrawing): Observable<Blob | ArrayBuffer> {
        this.crudService.logDirect(TechnicalFeature.Whiteboard);

        const route = this.routeService.getAbsoluteRoute(
            environment.endpointUrls.whiteboardDrawingDownload, { bookId: book, id: whiteboard.id });

        return this.httpClient.get<string>(
            route,
            // @ts-ignore
            { responseType: 'blob' }
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.closeSubscription.unsubscribe();
        this.openSubscription.unsubscribe();
        this.openVideoSubscription.unsubscribe();
    }
}
